import React from 'react';
import { Menu, X, ExternalLink } from 'lucide-react';
import { PersonalLogo } from './PersonalLogo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';

const RAINBOW_ROOM_URL = 'https://rainbow-room-seven.vercel.app/';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.dispatchEvent(new CustomEvent('petechtor:open-section', { detail: id }));
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (window.history?.replaceState) {
        window.history.replaceState(null, '', `#${id}`);
      }
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-slate-950/95 border-b border-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <PersonalLogo size="medium" />
            <div className="leading-tight">
              <div className="text-sm font-extrabold tracking-wider text-white">{t('petechtor')}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide">{t('guardiansOfPets')}</div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 text-xs uppercase tracking-wider">
            <a
              href={RAINBOW_ROOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-rainbow-room-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors duration-200"
            >
              {t('rainbowRoomNav')}
              <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={() => scrollToSection('how')}
              className="px-4 py-2 rounded-full text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors duration-200"
            >
              {t('howItWorksNav')}
            </button>
            
            <LanguageSwitcher />
            
            <button
              onClick={() => scrollToSection('waitlist')}
              className="ml-2 px-5 py-2 rounded-full text-slate-900 bg-gradient-to-r from-pink-500 to-cyan-400 font-semibold shadow-md hover:shadow-lg transition-shadow duration-200"
            >
              {t('joinGuardians')}
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-4 animate-in slide-in-from-top duration-200">
            <nav className="flex flex-col gap-2 text-sm">
              <a
                href={RAINBOW_ROOM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-mobile-rainbow-room-link"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-left text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors"
              >
                {t('rainbowRoomNav')}
                <ExternalLink className="w-3 h-3" />
              </a>
              <button
                onClick={() => scrollToSection('how')}
                className="px-4 py-2 rounded-lg text-left text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors"
              >
                {t('howItWorksNav')}
              </button>
              <button
                onClick={() => scrollToSection('waitlist')}
                className="px-4 py-2 rounded-lg text-slate-900 bg-gradient-to-r from-pink-500 to-cyan-400 font-semibold transition-all duration-200"
              >
                {t('joinGuardians')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
